import { useState, useEffect, useContext } from 'react';
import { AuthContext, apiClient } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Trophy, Award, Medal, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const Rankings = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState('High School');
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followingMap, setFollowingMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});

  // Admin-only access guard. Leaderboard is buried for the early TestFlight cohort
  // and only reachable by admin/scout users while the feature is refined.
  // Matches the SMSMonitoring guard pattern.
  useEffect(() => {
    if (user && !['admin', 'scout'].includes(user.role)) {
      toast.error('Page not available');
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (selectedGroup) {
      fetchRankings();
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (rankings.length > 0) {
      fetchFollowing();
    }
  }, [rankings]);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/rankings?age_group=${encodeURIComponent(selectedGroup)}`);
      setRankings(response.data);
    } catch (error) {
      toast.error('Failed to fetch rankings');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowing = async () => {
    try {
      const response = await apiClient.get('/follow/following');
      const map = {};
      for (const u of response.data.following || []) {
        map[u.id] = true;
      }
      setFollowingMap(map);
    } catch (error) {
      // Non-fatal, just continue without follow status
    }
  };

  const doFollow = async (userId, username) => {
    if (!userId) {
      toast.error('Invalid user ID');
      return;
    }
    setLoadingMap(prev => ({ ...prev, [userId]: true }));
    try {
      await apiClient.post(`/follow/${userId}`);
      setFollowingMap(prev => ({ ...prev, [userId]: true }));
      toast.success(`Following ${username}`);
    } catch (error) {
      const msg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error';
      toast.error(`Follow failed: ${msg}`);
      console.error('Follow error', error?.response?.data || error);
      console.error('Follow error', error?.response?.data || error);
    } finally {
      setLoadingMap(prev => ({ ...prev, [userId]: false }));
    }
  };

  const doUnfollow = async (userId, username) => {
    if (!userId) {
      toast.error('Invalid user ID');
      return;
    }
    setLoadingMap(prev => ({ ...prev, [userId]: true }));
    try {
      await apiClient.delete(`/follow/${userId}`);
      setFollowingMap(prev => ({ ...prev, [userId]: false }));
      toast.success(`Unfollowed ${username}`);
    } catch (error) {
      const msg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Unknown error';
      toast.error(`Unfollow failed: ${msg}`);
      console.error('Unfollow error', error?.response?.data || error);
      console.error('Unfollow error', error?.response?.data || error);
    } finally {
      setLoadingMap(prev => ({ ...prev, [userId]: false }));
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'Elite':
        return <Trophy className="w-5 h-5 text-purple-600" />;
      case 'Competitive':
        return <Award className="w-5 h-5 text-orange-600" />;
      case 'Role Player':
        return <Medal className="w-5 h-5 text-blue-600" />;
      default:
        return <TrendingUp className="w-5 h-5 text-gray-400" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Elite':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Competitive':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Role Player':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const getRankBadge = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `#${index + 1}`;
  };

  const FollowButton = ({ player }) => {
    if (!user || player.id === user.id) return null;
    
    const isFollowing = followingMap[player.id];
    const isLoading = loadingMap[player.id];

    return (
      <Button
        size="sm"
        variant={isFollowing ? "outline" : "default"}
        disabled={isLoading}
        onClick={() => isFollowing ? doUnfollow(player.id, player.username) : doFollow(player.id, player.username)}
      >
        {isLoading ? "..." : isFollowing ? "Following" : "Follow"}
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FFFFFF] rounded-[12px] gap-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>

        <section className="bg-[#FFFFFF] rounded-[16px] border border-[#E4E4E7] p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-[#F97316] rounded-[12px] flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-[24px] sm:text-[32px] font-bold text-[#111111] tracking-[-0.02em]">Leaderboard</h1>
              <p className="text-[14px] font-medium text-[#52525B]">See how you rank against other players</p>
            </div>
          </div>

          {/* Age Group Selector */}
          <div className="mb-8">
            <label className="block text-[12px] font-semibold uppercase tracking-[0.05em] mb-2 text-[#71717A]">Select Player Group</label>
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger className="max-w-xs" data-testid="group-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Middle School">Middle School</SelectItem>
                <SelectItem value="High School">High School</SelectItem>
                <SelectItem value="College">College</SelectItem>
                <SelectItem value="23-34">23-34</SelectItem>
                <SelectItem value="35-49">35-49</SelectItem>
                <SelectItem value="50-75">50-75</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Rankings List */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading rankings...</p>
            </div>
          ) : rankings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No players in this group yet</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="rankings-list">
              {rankings.map((player, index) => (
                <div
                  key={player.id || player.user_id || `ranking-${index}`}
                  className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                    index < 3 ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300 shadow-sm' : 'bg-white border-gray-200'
                  }`}
                  data-testid={`ranking-item-${index}`}
                >
                  {/* Mobile Layout */}
                  <div className="sm:hidden">
                    <div className="flex items-start gap-3">
                      {/* Rank/Medal */}
                      <div className="text-xl font-bold w-10 text-center flex-shrink-0 pt-0.5">
                        {getRankBadge(index)}
                      </div>
                      
                      {/* Player Info - Center */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p 
                            className="font-semibold text-gray-900 hover:text-orange-600 cursor-pointer transition-colors truncate"
                            onClick={() => navigate(`/player/${player.username}`)}
                          >
                            {player.username}
                          </p>
                          {player.is_certified_elite && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold rounded-full whitespace-nowrap">
                              ✓ Verified
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{player.age_group}</p>
                        
                        {/* Tier badge on mobile - below name */}
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getTierColor(player.player_tier)}`}>
                            {player.player_tier}
                          </span>
                          <span className="text-xs text-gray-500">
                            {player.win_count}-{player.loss_count} W-L
                          </span>
                        </div>
                      </div>
                      
                      {/* Score - Right */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl font-bold text-orange-600">{player.ranking_score}</p>
                        <p className="text-[10px] text-gray-500 uppercase">Score</p>
                      </div>
                    </div>
                    
                    {/* Follow button - Full width below on mobile */}
                    {user && player.id !== user.id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <Button
                          size="sm"
                          variant={followingMap[player.id] ? "outline" : "default"}
                          disabled={loadingMap[player.id]}
                          onClick={() => followingMap[player.id] ? doUnfollow(player.id, player.username) : doFollow(player.id, player.username)}
                          className="w-full h-8 text-xs"
                        >
                          {loadingMap[player.id] ? "..." : followingMap[player.id] ? "Following" : "Follow"}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="text-2xl font-bold w-12 text-center">
                        {getRankBadge(index)}
                      </div>
                      <div className="flex items-center gap-2">
                        {getTierIcon(player.player_tier)}
                        <div>
                          <div className="flex items-center gap-2">
                            <p 
                              className="font-bold text-gray-900 hover:text-orange-600 cursor-pointer transition-colors"
                              onClick={() => navigate(`/player/${player.username}`)}
                            >
                              {player.username}
                            </p>
                            {player.is_certified_elite && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                                ✓ Verified
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{player.age_group}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className={`px-3 py-1 rounded-lg border-2 text-sm font-semibold ${getTierColor(player.player_tier)}`}>
                          {player.player_tier}
                        </p>
                      </div>
                      <div className="text-center min-w-[60px]">
                        <p className="text-2xl font-bold text-orange-600">{player.ranking_score}</p>
                        <p className="text-xs text-gray-500">Score</p>
                      </div>
                      <div className="text-center min-w-[80px]">
                        <p className="text-lg font-semibold text-gray-900">
                          {player.win_count}-{player.loss_count}
                        </p>
                        <p className="text-xs text-gray-500">W-L</p>
                      </div>
                      <div className="min-w-[100px]">
                        <FollowButton player={player} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Rankings;
